import { Id } from '@/infra/id'
import { collection } from '@/infra/collection'
import { onRpcNotFoundReturnNull } from '@/utils/grpc-util'
import { type JobsApi } from '@/features/jobs/api/job-api'
import {
  CreateBusinessProfileRequest,
  GetBusinessProfileForJobRequest,
  UpdateBusinessProfileRequest,
} from '@taxfyle/api-internal/internal/business_profile_pb'
import type { ContactInfo } from '@/features/jobs/info-gathering/ContactInfo'
import { mapContactInfoToProto } from '@/features/jobs/info-gathering/contact-info-mappers'
import type { BusinessInfo } from './BusinessInfo'
import {
  BusinessProfileVersion,
  BusinessProfileVersionId,
} from './BusinessProfileVersion'
import {
  mapBusinessInfoToProto,
  mapBusinessProfileVersionProtoToProps,
} from './business-profile-mappers'

/**
 * Manages the business profiles captured for jobs.
 */
export class BusinessProfileStore {
  readonly businessProfileVersions = collection({
    create: () => new BusinessProfileVersion(),
    getDataId: (d) => BusinessProfileVersionId.from(d.id, d.version),
    getModelId: (m) => BusinessProfileVersionId.from(m.id, m.version),
  })

  constructor(private readonly _api: JobsApi) {}

  /**
   * Gets a business profile version.
   */
  getBusinessProfile(id: Id, version: number) {
    return this.businessProfileVersions.get(
      BusinessProfileVersionId.from(id, version),
    )
  }

  /**
   * Fetches the business profile provided to a job.
   */
  async fetchBusinessProfileForJob(
    jobId: Id,
    signal?: AbortSignal,
  ): Promise<BusinessProfileVersion | null> {
    const request = new GetBusinessProfileForJobRequest().setJobId(
      Id.assert(jobId),
    )
    const response = await this._api.businessProfiles
      .getBusinessProfileForJob(request)
      .catch(onRpcNotFoundReturnNull)
    signal?.throwIfAborted()
    if (!response) {
      return null
    }

    return this.businessProfileVersions.set(
      mapBusinessProfileVersionProtoToProps(response),
    )
  }

  /**
   * Creates a business profile.
   */
  async createBusinessProfile(
    input: CreateBusinessProfileOptions,
  ): Promise<BusinessProfileVersion> {
    const request = new CreateBusinessProfileRequest()
      .setWorkspaceId(Id.assert(input.workspaceId))
      .setOwnerUserId(Id.assert(input.ownerUserId))
      .setBusinessInfo(mapBusinessInfoToProto(input.businessInfo))
      .setContactInfo(mapContactInfoToProto(input.contactInfo))

    const response =
      await this._api.businessProfiles.createBusinessProfile(request)

    return this.businessProfileVersions.set({
      id: Id(response.getId()),
      version: response.getVersion(),
      workspaceId: input.workspaceId,
      ownerUserId: input.ownerUserId,
      businessInfo: input.businessInfo,
      contactInfo: input.contactInfo,
    })
  }

  /**
   * Saves a new version of a business profile.
   */
  async updateBusinessProfile(
    input: UpdateBusinessProfileOptions,
  ): Promise<BusinessProfileVersion> {
    const request = new UpdateBusinessProfileRequest()
      .setId(Id.assert(input.id))
      .setBusinessInfo(mapBusinessInfoToProto(input.businessInfo))
      .setContactInfo(mapContactInfoToProto(input.contactInfo))

    const response =
      await this._api.businessProfiles.updateBusinessProfile(request)

    return this.businessProfileVersions.set({
      id: Id(response.getId()),
      version: response.getVersion(),
      workspaceId: input.workspaceId,
      ownerUserId: input.ownerUserId,
      businessInfo: input.businessInfo,
      contactInfo: input.contactInfo,
    })
  }
}

/**
 * Options for creating a business profile.
 */
export interface CreateBusinessProfileOptions {
  workspaceId: Id
  ownerUserId: Id
  businessInfo: BusinessInfo
  contactInfo: ContactInfo
}

/**
 * Options for saving a new version of a business profile.
 */
export interface UpdateBusinessProfileOptions extends CreateBusinessProfileOptions {
  id: Id
}
